import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Span,
  Label,
  Button,
  Select,
  TextArea,
  InnerPanel,
  CreatableSelect
} from '@sopranodesign/pulse-components';
import { TemplatingTextInput } from '@sopranodesign/pulse-templating';

import { getMcpTools } from '../../../../../../api/mcp';

const SCALAR_ARRAY_ITEM_TYPES = ['string', 'number', 'integer', 'boolean'];

// A row-per-item editor for array arguments whose items are objects (or have
// no declared shape at all, e.g. `list[dict[str, Any]]`) - the tag-style
// CreatableSelect used for scalar arrays can't represent these. Each row is
// free-form JSON; rows that parse become real objects in the submitted
// array, rows that don't (yet) are kept as the raw string.
const JsonArrayField = ({ label, itemValue, onChange }) => {
  const rowText = item => (typeof item === 'string' ? item : JSON.stringify(item));

  const [drafts, setDrafts] = useState(() =>
    Array.isArray(itemValue) ? itemValue.map(rowText) : []
  );

  const commit = nextDrafts => {
    setDrafts(nextDrafts);
    onChange(
      nextDrafts.map(text => {
        try {
          return JSON.parse(text);
        } catch {
          return text;
        }
      })
    );
  };

  const handleRowChange = index => e => {
    const next = [...drafts];
    next[index] = e.target.value;
    commit(next);
  };

  const handleAddRow = () => commit([...drafts, '']);
  const handleRemoveRow = index => commit(drafts.filter((_, i) => i !== index));

  return (
    <Flex col fat>
      {drafts.map((text, index) => (
        <Flex key={index} xSpace mb>
          <Flex fat>
            <TextArea
              aria-label={`${label}-${index}`}
              rows={3}
              placeholder='{"type": 3, "id": 42993}'
              value={text}
              onChange={handleRowChange(index)}
            />
          </Flex>
          <Button
            type="button"
            icon="trash"
            colour="danger"
            aria-label="Remove item"
            onClick={() => handleRemoveRow(index)}
          />
        </Flex>
      ))}
      <Button type="button" icon="plus" onClick={handleAddRow}>
        Add {label} item
      </Button>
    </Flex>
  );
};

JsonArrayField.propTypes = {
  label: PropTypes.string.isRequired,
  itemValue: PropTypes.array,
  onChange: PropTypes.func.isRequired
};

JsonArrayField.defaultProps = {
  itemValue: []
};

const InvokeMcpSkillContent = ({ content, onChange }) => {
  const [tools, setTools] = useState([]);

  useEffect(() => {
    getMcpTools().then(({ data }) => setTools(data.tools ?? []));
  }, []);

  const selectedTool = tools.find(t => t.name === content.skill_name) ?? null;

  const skillOptions = tools.map(t => ({ value: t.name, label: t.name }));

  // Resolves a property's JSON Schema type/enum, following the "anyOf" (e.g.
  // Optional[x]) and "$ref" (e.g. enum $defs) shapes that pydantic/Zod
  // schemas commonly produce instead of a plain top-level "type"/"enum" key.
  const resolveSchema = def => {
    if (def.type || def.enum) {
      return def;
    }

    const nonNullBranch = (def.anyOf ?? []).find(branch => branch.type !== 'null');
    if (nonNullBranch) {
      return nonNullBranch;
    }

    if (def.$ref) {
      const defName = def.$ref.replace('#/$defs/', '');
      return selectedTool?.inputSchema?.$defs?.[defName] ?? {};
    }

    return {};
  };

  const fields = selectedTool
    ? Object.entries(selectedTool.inputSchema?.properties ?? {}).map(
        ([key, def]) => {
          const resolved = resolveSchema(def);
          const itemDef = resolved.type === 'array' ? resolveSchema(resolved.items ?? {}) : null;

          return {
            key,
            label: def.description ?? key,
            required: (selectedTool.inputSchema?.required ?? []).includes(key),
            type: resolved.type ?? 'string',
            isObjectArray: itemDef ? !SCALAR_ARRAY_ITEM_TYPES.includes(itemDef.type) : false,
            enumOptions: Array.isArray(resolved.enum)
              ? resolved.enum.map(value => ({ value, label: value }))
              : null
          };
        }
      )
    : [];

  const handleSkillChange = ({ value }) => {
    onChange({ skill_name: value, arguments: {} });
  };

  const handleArgumentChange = key => value => {
    onChange({
      ...content,
      arguments: { ...(content.arguments ?? {}), [key]: value }
    });
  };

  const handleEnumArgumentChange = key => option => {
    onChange({
      ...content,
      arguments: { ...(content.arguments ?? {}), [key]: option?.value ?? '' }
    });
  };

  const handleArrayArgumentChange = key => selected => {
    onChange({
      ...content,
      arguments: {
        ...(content.arguments ?? {}),
        [key]: (selected ?? []).map(o => o.value)
      }
    });
  };

  const toArrayOptions = value => {
    if (Array.isArray(value)) {
      return value.map(v => ({ value: v, label: v }));
    }

    return [];
  };

  return (
    <Flex col fat xSpace>
      <Flex center fat="true" xSpace mb>
        <Span noWrap>using skill</Span>
        <Flex col fat>
          <Select
            aria-label="MCP skill"
            placeholder="Select a skill..."
            options={skillOptions}
            value={
              content.skill_name
                ? { value: content.skill_name, label: content.skill_name }
                : null
            }
            onChange={handleSkillChange}
          />
        </Flex>
      </Flex>

      {selectedTool?.description && (
       <Label>
        <Span light>
          {selectedTool.description}
        </Span>
       </Label>
      )}

      {selectedTool && (
        <InnerPanel>
          {fields.length === 0 && (
            <Span light mb>No arguments required.</Span>
          )}

          {fields.map((field, index) => (
            <Flex key={field.key} col fat mb={index < fields.length - 1}>
              <Label>
                {field.label}
              </Label>

              {field.type === 'array' && field.isObjectArray ? (
                <JsonArrayField
                  label={field.key}
                  itemValue={(content.arguments ?? {})[field.key]}
                  onChange={handleArgumentChange(field.key)}
                />
              ) : field.type === 'array' ? (
                <CreatableSelect
                  aria-label={field.label}
                  placeholder={`Type and press Enter to add ${field.key} values…`}
                  isMulti
                  isClearable
                  value={toArrayOptions((content.arguments ?? {})[field.key])}
                  onChange={handleArrayArgumentChange(field.key)}
                  noOptionsMessage={() => 'Type a value and press Enter'}
                />
              ) : field.enumOptions ? (
                <Select
                  aria-label={field.label}
                  placeholder={`Select ${field.key}...`}
                  options={field.enumOptions}
                  value={
                    field.enumOptions.find(
                      o => o.value === (content.arguments ?? {})[field.key]
                    ) ?? null
                  }
                  onChange={handleEnumArgumentChange(field.key)}
                />
              ) : (
                <TemplatingTextInput
                  aria-label={field.label}
                  placeholder={field.key}
                  value={(content.arguments ?? {})[field.key] ?? ''}
                  onChange={handleArgumentChange(field.key)}
                />
              )}
            </Flex>
          ))}
        </InnerPanel>
      )}
    </Flex>
  );
};

InvokeMcpSkillContent.propTypes = {
  onChange: PropTypes.func.isRequired,
  content: PropTypes.shape({
    skill_name: PropTypes.string,
    arguments: PropTypes.object
  }).isRequired
};

export default InvokeMcpSkillContent;
